import { EstadoExpediente } from "@copi/shared";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/auth.context";
import { aceptacionAsesorApi } from "@/lib/api/aceptacion-asesor";
import { asesoriaApi } from "@/lib/api/asesoria";
import { juradoApi } from "@/lib/api/jurado";
import { notificacionesApi } from "@/lib/api/notificaciones";
import { TIPOS_NOTIF_ASESOR, TIPOS_NOTIF_JURADO } from "@/lib/notificaciones-rol";

const STALE_MS = 30_000;

/** Estados donde el jurado tiene una revisión que requiere su acción. */
const ESTADOS_REVISION_JURADO: ReadonlySet<EstadoExpediente> = new Set([
  EstadoExpediente.PROYECTO_EN_REVISION_JURADO,
  EstadoExpediente.BORRADOR_EN_REVISION_JURADO,
]);

export interface ContadoresDocencia {
  /** Acciones pendientes como asesor: proyectos pidiendo revisión + propuestas nuevas. */
  revisionesAsesor: number;
  /** Acciones pendientes como jurado: tesis en ventana de revisión (proyecto/borrador). */
  revisionesJurado: number;
  /** Hay una notificación NO LEÍDA de rol asesor (designación, propuesta, pedido de revisión). */
  nuevoAsesor: boolean;
  /** Hay una notificación NO LEÍDA de rol jurado (designación). */
  nuevoJurado: boolean;
}

/**
 * Contadores de "pendientes" del docente para los badges del sidebar (ADR 054).
 *
 * Reusa las MISMAS queries que las bandejas (`["asesoria","mis-estudiantes"]`,
 * `["jurado","mis-asignaciones"]`, `["aceptaciones-asesor","mias"]`), así el SSE
 * (que ya invalida esos prefijos en `expediente.estado-cambio` /
 * `notificacion.nueva`) actualiza los badges EN VIVO sin cableado extra. Cada
 * query se habilita sólo si el docente tiene la capacidad correspondiente.
 */
export function useContadoresDocencia(): ContadoresDocencia {
  const { user } = useAuth();
  const puedeAsesorar = Boolean(user?.puedeAsesorar);
  const puedeSerJurado = Boolean(user?.puedeSerJurado);

  const asesoria = useQuery({
    queryKey: ["asesoria", "mis-estudiantes"],
    queryFn: () => asesoriaApi.misEstudiantes(),
    enabled: puedeAsesorar,
    staleTime: STALE_MS,
  });
  const propuestas = useQuery({
    queryKey: ["aceptaciones-asesor", "mias"],
    queryFn: () => aceptacionAsesorApi.mias(),
    enabled: puedeAsesorar,
    staleTime: STALE_MS,
  });
  const jurado = useQuery({
    queryKey: ["jurado", "mis-asignaciones"],
    queryFn: () => juradoApi.misAsignaciones(),
    enabled: puedeSerJurado,
    staleTime: STALE_MS,
  });
  // Notificaciones NO LEÍDAS (misma query que la campana, ya invalidada por SSE)
  // para encender el punto "nuevo" por rol. Sólo se consulta si es docente.
  const notificaciones = useQuery({
    queryKey: ["notificaciones", "mias"],
    queryFn: () => notificacionesApi.mias(),
    enabled: puedeAsesorar || puedeSerJurado,
    staleTime: STALE_MS,
  });

  const pidiendoRevision = (asesoria.data ?? []).filter(
    (i) => i.expediente.estadoActual === EstadoExpediente.PROYECTO_EN_REVISION_ASESOR,
  ).length;
  const propuestasPendientes = (propuestas.data ?? []).filter(
    (p) => p.estado === "PENDIENTE",
  ).length;
  const revisionesJurado = (jurado.data ?? []).filter((i) =>
    ESTADOS_REVISION_JURADO.has(i.expediente.estadoActual),
  ).length;

  const noLeidas = notificaciones.data ?? [];
  const nuevoAsesor = puedeAsesorar && noLeidas.some((n) => TIPOS_NOTIF_ASESOR.has(n.tipoEvento));
  const nuevoJurado = puedeSerJurado && noLeidas.some((n) => TIPOS_NOTIF_JURADO.has(n.tipoEvento));

  return {
    revisionesAsesor: pidiendoRevision + propuestasPendientes,
    revisionesJurado,
    nuevoAsesor,
    nuevoJurado,
  };
}
